import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

interface PartsPullItem {
  id: string;
  partNumber: string;
  description: string;
  quantityNeeded: number;
  quantityPulled: number;
  pulled: boolean;
  shortage: boolean;
  shortageQuantity: number | null;
  binLocation: string | null;
  componentType: string;
  imageUrl: string | null;
}

interface PartsPull {
  id: string;
  orderId: string;
  orderNumber: string;
  pullType: 'bin' | 'shelves';
  status: 'pending' | 'in_progress' | 'complete' | 'shortage';
  binColor: string | null;
  assignedBinId: string | null;
  items: PartsPullItem[];
  startedAt: string | null;
  completedAt: string | null;
}

interface PartsPullQueueItem {
  id: string;
  orderId: string;
  orderNumber: string;
  companyName: string;
  sidemark: string;
  pullType: 'bin' | 'shelves';
  status: string;
  unitCount: number;
  itemCount: number;
  isRush: boolean;
}

export function PartsPullPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId?: string }>();

  const [pullType, setPullType] = useState<'bin' | 'shelves'>('bin');
  const [queue, setQueue] = useState<PartsPullQueueItem[]>([]);
  const [activePull, setActivePull] = useState<PartsPull | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showShortageModal, setShowShortageModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PartsPullItem | null>(null);
  const [shortageQty, setShortageQty] = useState<number>(0);
  const [shortageNotes, setShortageNotes] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // Load queue or active pull
  useEffect(() => {
    if (orderId) {
      loadActivePull(orderId);
    } else {
      loadQueue();
    }
  }, [orderId, pullType]);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/parts-pull/queue/${pullType}`);
      setQueue(response.data);
    } catch (error) {
      console.error('Failed to load queue:', error);
      showToast('Failed to load queue', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadActivePull = async (orderId: string) => {
    setLoading(true);
    try {
      const response = await api.get(`/orders/${orderId}/parts-pull`);
      // API returns { orderId, orderNumber, bin: {...}, shelves: {...} }
      const summary = response.data;
      const pull = pullType === 'bin' ? summary.bin : summary.shelves;

      // Auto-start pull if still pending
      if (pull && pull.status === 'pending') {
        try {
          await api.post(`/orders/${orderId}/parts-pull/${pullType}/start`, {});
          // Reload to get updated status
          const updated = await api.get(`/orders/${orderId}/parts-pull`);
          const updatedPull = pullType === 'bin' ? updated.data.bin : updated.data.shelves;
          setActivePull(updatedPull || pull);
        } catch {
          // Start failed, still show the items
          setActivePull(pull);
        }
      } else {
        setActivePull(pull || null);
      }
    } catch (error) {
      console.error('Failed to load pull:', error);
      showToast('Failed to load parts list', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPull = (queueItem: PartsPullQueueItem) => {
    navigate(`/parts-pull/${queueItem.orderId}`);
  };

  const handleMarkPulled = async (item: PartsPullItem) => {
    if (!activePull) return;

    try {
      await api.post(`/parts-pull/items/${item.id}/pulled`, {
        quantity: item.quantityNeeded
      });

      // Update item in-place to avoid scroll-to-top on full reload
      setActivePull((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i: PartsPullItem) =>
            i.id === item.id
              ? { ...i, pulled: true, quantityPulled: item.quantityNeeded }
              : i
          ),
        };
      });
      showToast(`${item.partNumber} pulled`, 'success');
    } catch (error) {
      console.error('Failed to mark pulled:', error);
      showToast('Failed to mark item pulled', 'error');
    }
  };

  const handleOpenShortage = (item: PartsPullItem) => {
    setSelectedItem(item);
    setShortageQty(item.quantityNeeded - item.quantityPulled);
    setShortageNotes('');
    setShowShortageModal(true);
  };

  const handleSubmitShortage = async () => {
    if (!selectedItem || !activePull) return;

    try {
      await api.post(`/parts-pull/items/${selectedItem.id}/shortage`, {
        shortageQuantity: shortageQty,
        notes: shortageNotes
      });

      // Update item in-place to avoid scroll-to-top on full reload
      const pulledQty = selectedItem.quantityNeeded - shortageQty;
      setActivePull((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i: PartsPullItem) =>
            i.id === selectedItem.id
              ? { ...i, pulled: true, shortage: true, shortageQuantity: shortageQty, quantityPulled: pulledQty }
              : i
          ),
        };
      });
      setShowShortageModal(false);
      setSelectedItem(null);
      showToast('Shortage reported', 'success');
    } catch (error) {
      console.error('Failed to report shortage:', error);
      showToast('Failed to report shortage', 'error');
    }
  };

  const handleCompletePull = async (binColor: 'black' | 'red') => {
    if (!activePull) return;

    try {
      await api.post(`/orders/${activePull.orderId}/parts-pull/${pullType}/complete`, {
        binColor
      });

      setShowCompleteModal(false);
      showToast(
        pullType === 'shelves'
          ? hasShortages ? 'Shelf pull complete - shortages flagged' : 'Shelf pull complete - parts on shelf'
          : `Pull complete - ${binColor.toUpperCase()} bin`,
        'success'
      );
      navigate('/parts-pull');
    } catch (error) {
      console.error('Failed to complete pull:', error);
      showToast('Failed to complete pull', 'error');
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getProgress = useCallback(() => {
    if (!activePull) return { pulled: 0, total: 0, percentage: 0 };
    const pulled = activePull.items.filter(i => i.pulled || i.shortage).length;
    const total = activePull.items.length;
    return { pulled, total, percentage: total > 0 ? Math.round((pulled / total) * 100) : 0 };
  }, [activePull]);

  // Category display order for BIN and SHELVES pulls
  const CATEGORY_ORDER: Record<string, string[]> = {
    bin: ['TRACKS', 'CUSTOMER BAG', 'WEIGHT BAR', 'MATERIAL'],
    shelves: ['ROLLER TUBE', 'BOX/POCKET', 'WEIGHT BAR'],
  };

  const groupedItems = useCallback(() => {
    if (!activePull) return {};
    const groups = activePull.items.reduce((acc, item) => {
      const type = item.componentType || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {} as Record<string, PartsPullItem[]>);

    // Sort groups by predefined category order
    const order = CATEGORY_ORDER[pullType] || [];
    const sorted: Record<string, PartsPullItem[]> = {};
    for (const cat of order) {
      if (groups[cat]) {
        sorted[cat] = groups[cat];
        delete groups[cat];
      }
    }
    // Append any remaining categories not in the predefined order
    for (const [key, val] of Object.entries(groups)) {
      sorted[key] = val;
    }
    return sorted;
  }, [activePull, pullType]);

  const getImageUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `https://res.cloudinary.com/keder-screens/image/upload/${url}`;
  };

  // Loading
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Queue view (no active pull)
  if (!orderId || !activePull) {
    return (
      <div className="min-h-screen bg-gray-900 pb-20">
        {/* Header */}
        <div className="bg-gray-800 px-4 py-3 sticky top-0 z-10">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-white">Parts Pull</h1>
          </div>

          {/* Pull Type Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setPullType('bin')}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                pullType === 'bin'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-gray-700 text-gray-300'
              }`}
            >
              BIN Parts
            </button>
            <button
              onClick={() => setPullType('shelves')}
              className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                pullType === 'shelves'
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-gray-700 text-gray-300'
              }`}
            >
              SHELF Parts
            </button>
          </div>
        </div>

        {/* Queue List */}
        <div className="p-4 space-y-3">
          {queue.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">
                {pullType === 'bin' ? '📦' : '🗄️'}
              </div>
              <div className="text-gray-400">No orders waiting for {pullType} pull</div>
            </div>
          ) : (
            queue.map(item => (
              <button
                key={item.id}
                onClick={() => handleOpenPull(item)}
                className={`w-full p-4 rounded-xl text-left transition-colors ${
                  item.isRush
                    ? 'bg-red-900 border-2 border-red-500'
                    : 'bg-gray-800 hover:bg-gray-750'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-lg font-bold text-white">
                      #{item.orderNumber}
                    </div>
                    <div className="text-gray-400">{item.companyName}</div>
                    {item.sidemark && (
                      <div className="text-gray-500 text-sm">{item.sidemark}</div>
                    )}
                  </div>
                  <div className="text-right">
                    {item.isRush && (
                      <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded mb-1 inline-block">
                        RUSH
                      </span>
                    )}
                    <div className="text-gray-400 text-sm">
                      {item.unitCount} unit{item.unitCount !== 1 ? 's' : ''} &bull; {item.itemCount} parts
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  // If pull is already complete (e.g. shortage resolved from admin), show done state
  if (activePull.status === 'complete') {
    return (
      <div className="fixed inset-0 bg-gray-900 flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">{activePull.binColor === 'red' ? '🔴' : '⬛'}</div>
        <h2 className="text-2xl font-bold text-white mb-2">Pull Complete</h2>
        <p className="text-gray-400 mb-2">#{activePull.orderNumber} — {pullType.toUpperCase()} pull</p>
        <p className="text-gray-500 mb-6">
          {activePull.binColor === 'black' ? 'BLACK bin — all parts pulled' : 'RED bin — has shortages'}
        </p>
        <button
          onClick={() => navigate('/parts-pull')}
          className="px-6 py-3 bg-yellow-500 text-gray-900 font-bold rounded-xl"
        >
          Back to Queue
        </button>
      </div>
    );
  }

  // Active pull view
  const progress = getProgress();
  const groups = groupedItems();
  const hasShortages = activePull.items.some(i => i.shortage);
  const allComplete = progress.pulled === progress.total;

  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/parts-pull')}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="text-white font-bold">#{activePull.orderNumber}</div>
              <div className="text-gray-400 text-sm uppercase">{pullType} Pull</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-yellow-400 font-bold">{progress.pulled}/{progress.total}</div>
            <div className="text-gray-500 text-xs">{progress.percentage}% complete</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all ${hasShortages ? 'bg-red-500' : 'bg-green-500'}`}
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
      </div>

      {/* Pull Type Tabs (if viewing active pull, allow switching) */}
      <div className="bg-gray-800 px-4 pb-3 flex gap-2">
        <button
          onClick={() => setPullType('bin')}
          className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
            pullType === 'bin'
              ? 'bg-yellow-500 text-gray-900'
              : 'bg-gray-700 text-gray-300'
          }`}
        >
          BIN
        </button>
        <button
          onClick={() => setPullType('shelves')}
          className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
            pullType === 'shelves'
              ? 'bg-yellow-500 text-gray-900'
              : 'bg-gray-700 text-gray-300'
          }`}
        >
          SHELVES
        </button>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {Object.entries(groups).map(([type, items]) => (
          <div key={type}>
            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1">
              {type}
            </div>
            <div className="space-y-2">
              {items.map(item => (
                <div
                  key={item.id}
                  className={`rounded-xl p-3 transition-all ${
                    item.pulled
                      ? 'bg-green-900/50 border border-green-700'
                      : item.shortage
                        ? 'bg-red-900/50 border border-red-700'
                        : 'bg-gray-800'
                  }`}
                >
                  <div className="flex gap-3">
                    {/* Image or placeholder */}
                    <div className="w-16 h-16 flex-shrink-0 bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center">
                      {item.imageUrl ? (
                        <img
                          src={getImageUrl(item.imageUrl)!}
                          alt={item.partNumber}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl">
                          {item.pulled ? '✓' : item.shortage ? '!' : '📦'}
                        </span>
                      )}
                    </div>

                    {/* Part info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-white font-bold">{item.partNumber}</div>
                          <div className="text-gray-400 text-sm truncate">{item.description}</div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <div className="text-lg font-bold text-yellow-400">
                            x{item.quantityNeeded}
                          </div>
                          {item.binLocation && (
                            <div className="text-xs text-gray-500">{item.binLocation}</div>
                          )}
                        </div>
                      </div>

                      {/* Status or action buttons */}
                      {item.pulled ? (
                        <div className="mt-2 text-green-400 text-sm font-medium">
                          Pulled
                        </div>
                      ) : item.shortage ? (
                        <div className="mt-2 text-red-400 text-sm font-medium">
                          Shortage: {item.shortageQuantity} missing
                        </div>
                      ) : (
                        <div className="mt-2 flex gap-2">
                          <button
                            onClick={() => handleMarkPulled(item)}
                            className="flex-1 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
                          >
                            Pull
                          </button>
                          <button
                            onClick={() => handleOpenShortage(item)}
                            className="py-2 px-3 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                          >
                            Shortage
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom Action */}
      <div className="bg-gray-800 p-4">
        <button
          onClick={() => setShowCompleteModal(true)}
          disabled={!allComplete}
          className={`w-full py-4 text-white text-lg font-bold rounded-xl transition-colors ${
            allComplete
              ? hasShortages
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
              : 'bg-gray-600 cursor-not-allowed'
          }`}
        >
          {allComplete
            ? hasShortages
              ? 'Complete with Shortages'
              : 'Complete Pull'
            : `${progress.total - progress.pulled} items remaining`
          }
        </button>
      </div>

      {/* Shortage Modal */}
      {showShortageModal && selectedItem && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-4">Report Shortage</h2>
            <div className="mb-4">
              <div className="text-gray-400 text-sm">Part</div>
              <div className="text-white font-bold">{selectedItem.partNumber}</div>
              <div className="text-gray-400 text-sm">{selectedItem.description}</div>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-1">How many are missing?</label>
              <input
                type="number"
                value={shortageQty}
                onChange={(e) => setShortageQty(parseInt(e.target.value) || 0)}
                min={1}
                max={selectedItem.quantityNeeded}
                className="w-full p-3 bg-gray-700 text-white rounded-lg"
              />
              <div className="text-gray-500 text-xs mt-1">
                Needed: {selectedItem.quantityNeeded}
              </div>
            </div>
            <div className="mb-4">
              <label className="text-gray-400 text-sm block mb-1">Notes (optional)</label>
              <textarea
                value={shortageNotes}
                onChange={(e) => setShortageNotes(e.target.value)}
                placeholder="e.g., Only found 2 in bin"
                className="w-full p-3 bg-gray-700 text-white rounded-lg"
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowShortageModal(false)}
                className="flex-1 py-3 bg-gray-600 text-white rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitShortage}
                className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl"
              >
                Report Shortage
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-white mb-2">
              {pullType === 'shelves' ? 'Complete Shelf Pull' : 'Complete Bin Pull'}
            </h2>

            {pullType === 'shelves' ? (
              <>
                <p className="text-gray-400 mb-6">
                  {hasShortages
                    ? 'This pull has shortages. Parts will be flagged for supervisor review.'
                    : 'All parts pulled successfully and placed on shelf.'
                  }
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => handleCompletePull(hasShortages ? 'red' : 'black')}
                    className={`w-full py-4 text-white font-bold rounded-xl flex items-center justify-center gap-3 ${
                      hasShortages
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {hasShortages ? (
                      <>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Confirm - Flag Shortages
                      </>
                    ) : (
                      <>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Confirm - Parts on Shelf
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowCompleteModal(false)}
                    className="w-full py-3 bg-gray-600 text-white rounded-xl"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-400 mb-6">
                  {hasShortages
                    ? 'This pull has shortages. Select the bin color for staging.'
                    : 'All parts pulled successfully. Select the bin color for staging.'
                  }
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => handleCompletePull('black')}
                    className="w-full py-4 bg-gray-700 text-white font-bold rounded-xl flex items-center justify-center gap-3 hover:bg-gray-600"
                  >
                    <div className="w-8 h-8 bg-gray-900 rounded-full border-2 border-gray-500" />
                    BLACK Bin - All Good
                  </button>
                  <button
                    onClick={() => handleCompletePull('red')}
                    className="w-full py-4 bg-red-900 text-white font-bold rounded-xl flex items-center justify-center gap-3 hover:bg-red-800"
                  >
                    <div className="w-8 h-8 bg-red-600 rounded-full border-2 border-red-400" />
                    RED Bin - Has Shortages
                  </button>
                  <button
                    onClick={() => setShowCompleteModal(false)}
                    className="w-full py-3 bg-gray-600 text-white rounded-xl"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-24 left-4 right-4 p-4 rounded-xl shadow-lg z-50 ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          } text-white text-center font-medium`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default PartsPullPage;
